import { num } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Layers } from "lucide-react";

export const StackBreakdown = ({ stack, calculatedClass }) => {
  if (!stack) return null;

  const lineItems = stack.line_items || [];
  const rows = [];

  if (lineItems.length) {
    lineItems.forEach((it, i) => {
      rows.push({
        n: i + 1,
        label: it.product_name || it.description || it.handling_unit_type || `Item ${i + 1}`,
        type: it.handling_unit_type || "Pallet",
        units: it.units || 1,
        pieces: it.pieces,
        width: it.width,
        length: it.length,
        height: it.height,
        weight: it.weight,
        density: it.density,
        cls: it.fak_applied ? it.fak_class : it.freight_class,
        fak: it.fak_applied,
      });
    });
  } else {
    const fs = stack.full_stack;
    const ls = stack.last_stack;
    const fc = stack.full_stack_count || 0;
    const mk = (s, label, type) => ({
      label, type, units: 1, pieces: s?.pieces, width: s?.width, length: s?.length,
      height: s?.height, weight: s?.weight, density: s?.density,
      cls: s?.freight_class || calculatedClass,
    });
    for (let i = 0; i < fc; i++) rows.push({ n: i + 1, ...mk(fs, "Full Stack", "Full") });
    if (ls) rows.push({ n: fc + 1, ...mk(ls, "Partial Stack", "Partial") });
  }

  if (!rows.length) return null;

  const limit = stack.height_limit;
  const totalUnits = rows.reduce((a, r) => a + (r.units || 0), 0);
  const totalPieces = rows.reduce((a, r) => a + (r.pieces || 0), 0);
  const totalWeight = rows.reduce((a, r) => a + (r.weight || 0), 0);
  const partialTypes = new Set(["Partial", "partial"]);

  return (
    <div className="border border-border bg-card" data-testid="stack-breakdown">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight">
          <Layers className="h-4 w-4 text-[hsl(var(--primary))]" /> Individual Stack Breakdown
        </h3>
        <span className="label-caps">{rows.length} {rows.length === 1 ? "line" : "lines"}</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="w-10">#</TableHead>
              <TableHead>Handling Unit</TableHead>
              <TableHead className="text-right">Units</TableHead>
              <TableHead className="text-right">Pieces</TableHead>
              <TableHead>Dimensions (W×L×H)</TableHead>
              <TableHead className="text-right">Height</TableHead>
              <TableHead className="text-right">Weight</TableHead>
              <TableHead className="text-right">Density</TableHead>
              <TableHead className="text-right">Class</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const over = limit && r.height > limit;
              return (
                <TableRow key={r.n} className="border-border" data-testid={`stack-row-${r.n}`}>
                  <TableCell className="metric-num text-muted-foreground">{r.n}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{r.label}</span>
                      <span
                        className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                          partialTypes.has(r.type)
                            ? "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"
                            : "bg-secondary text-muted-foreground"
                        }`}
                      >
                        {r.type}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="metric-num text-right">{num(r.units)}</TableCell>
                  <TableCell className="metric-num text-right">{num(r.pieces)}</TableCell>
                  <TableCell className="text-sm">{num(r.width)}×{num(r.length)}×{num(r.height, 1)}"</TableCell>
                  <TableCell className={`metric-num text-right ${over ? "font-bold text-[hsl(var(--destructive))]" : ""}`}>{num(r.height, 1)}"</TableCell>
                  <TableCell className="metric-num text-right">{num(r.weight)} lb</TableCell>
                  <TableCell className="metric-num text-right">{num(r.density, 2)}</TableCell>
                  <TableCell className="text-right">
                    <span className="metric-num font-bold text-[hsl(var(--primary))]">{r.cls || "—"}</span>
                    {r.fak && <span className="ml-1 text-[9px] font-bold uppercase text-[hsl(var(--warning))]">FAK</span>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
        <span>{num(totalUnits)} handling {totalUnits === 1 ? "unit" : "units"} · {num(totalPieces)} pieces</span>
        <span className="metric-num">Total {num(totalWeight)} lb</span>
      </div>
    </div>
  );
};

export default StackBreakdown;
